// src/services/musicService.js
import { userPool } from './api';
import { logger } from '../utils/logger';

// Caching and preloading are removed as they are not used with direct URL streaming.

// export const clearSongCache = () => {
//   logger.debug('clearSongCache called (cache is currently disabled)');
// };

/**
 * Get the backend endpoint URL for streaming a song
 * @param {string} songId - The ID of the song to stream
 * @returns {Promise<{url: string}>} - The signed URL for streaming
 */
export const getStreamingUrl = async (songId) => {
  try {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      throw new Error('No authenticated user found');
    }

    const session = await new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => {
        if (err) reject(err);
        else resolve(session);
      });
    });

    // Return the relative API path, the base URL will be added by the caller (api instance)
    return {
      url: `/api/music/stream/${songId}`
    };
  } catch (error) {
    logger.error('Failed to get streaming URL', {
      error: error.message,
      songId
    });
    throw error;
  }
};

/**
 * Stream a song using the provided URL
 * @param {string} url - The URL to stream from
 * @param {string} absoluteUrl - The absolute URL to stream from (e.g., https://api.eduvibz.com/api/music/stream/...)
 * @param {string} absoluteUrl - The absolute URL to stream from (e.g., https://api.eduvibz.com/api/music/stream/...)
 * @param {string} backendStreamUrl - The backend endpoint URL (e.g., http://localhost:5000/api/music/stream/...)
 * @returns {Promise<string>} - The backend URL itself, letting the audio element handle the redirect.
 */
export const streamSong = async (backendStreamUrl) => {
  // Caching logic removed for testing
  try {
    // We no longer fetch the blob here. Instead, we return the URL
    // that the backend will use to redirect to the actual audio file (e.g., R2 presigned URL).
    // The HTML <audio> element's `src` attribute will be set to this URL,
    // and the browser will handle following the 302 redirect from the backend.

    // We still need the token to authorize the *initial* request to our backend API endpoint.
    // The <audio> element won't send this token when following the redirect,
    // but the presigned R2 URL itself contains the necessary authentication.
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      throw new Error('No authenticated user found for streaming');
    }

    // Ensure the user has a valid session to get the token
    await new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => {
        if (err) reject(err);
        else resolve(session);
      });
    });
    // We don't actually need the token variable itself here anymore,
    // but keeping the session check ensures the user is logged in.

    logger.info('Providing initial stream URL to audio element', { url: backendStreamUrl });

    // Return the backend URL directly. The <audio> tag will make a request to this URL.
    // The backend should verify the auth (using the token the browser sends with this request)
    // and respond with a 302 redirect to the R2 presigned URL.
    // The browser's <audio> element will then follow this redirect to fetch the actual audio data from R2.
    return backendStreamUrl;

  } catch (error) {
    logger.error('Failed to prepare stream URL', {
      error: error.message,
      initialUrl: backendStreamUrl
    });
    throw error; // Re-throw the error
  }
};

/**
 * Preload a song in the background
 * @param {string} songId - The ID of the song to preload
 * @returns {Promise<void>}
 */
// export const preloadSong = async (songId) => {
//   // Preloading is removed as it's not used with direct URL streaming.
//   logger.debug('Preloading removed', { songId });
// };
